import apiRequest from './api-request';

export type TenantArticleSection = {
    subtitle: string;
    body: string;
};

export type TenantArticle = {
    id: number;
    tenantId: number;
    slug: string;
    title: string;
    excerpt: string;
    metaTitle: string | null;
    metaDescription: string | null;
    coverImageUrl: string | null;
    sections: TenantArticleSection[];
    status: 'draft' | 'published';
    publishedAt: string | null;
    createdAt: string;
    updatedAt: string | null;
};

export type TenantArticleInput = {
    title: string;
    slug?: string;
    excerpt?: string;
    metaTitle?: string | null;
    metaDescription?: string | null;
    sections: TenantArticleSection[];
};

function tenantArticlesResource(tenantId: number | string) {
    return `tenants/${tenantId}/articles`;
}

export type TenantArticleListItem = TenantArticle & {
    tenantName: string;
};

export function getAllTenantArticles() {
    return apiRequest({
        method: 'GET',
        resource: 'articles'
    }) as Promise<TenantArticleListItem[]>;
}

export function getTenantArticles(tenantId: number | string) {
    return apiRequest({
        method: 'GET',
        resource: tenantArticlesResource(tenantId)
    }) as Promise<TenantArticle[]>;
}

export function getTenantArticle(tenantId: number | string, articleId: number | string) {
    return apiRequest({
        method: 'GET',
        resource: `${tenantArticlesResource(tenantId)}/${articleId}`
    }) as Promise<TenantArticle>;
}

export function createTenantArticle(tenantId: number | string, body: TenantArticleInput) {
    return apiRequest({
        method: 'POST',
        resource: tenantArticlesResource(tenantId),
        body
    }) as Promise<TenantArticle>;
}

export function updateTenantArticle(
    tenantId: number | string,
    articleId: number | string,
    body: TenantArticleInput
) {
    return apiRequest({
        method: 'PUT',
        resource: `${tenantArticlesResource(tenantId)}/${articleId}`,
        body
    }) as Promise<TenantArticle>;
}

export function publishTenantArticle(tenantId: number | string, articleId: number | string) {
    return apiRequest({
        method: 'PUT',
        resource: `${tenantArticlesResource(tenantId)}/${articleId}/publish`
    }) as Promise<TenantArticle>;
}

export function unpublishTenantArticle(tenantId: number | string, articleId: number | string) {
    return apiRequest({
        method: 'PUT',
        resource: `${tenantArticlesResource(tenantId)}/${articleId}/unpublish`
    }) as Promise<TenantArticle>;
}

export function deleteTenantArticle(tenantId: number | string, articleId: number | string) {
    return apiRequest({
        method: 'DELETE',
        resource: `${tenantArticlesResource(tenantId)}/${articleId}`
    });
}

export function getPresignedArticleCoverUrl(
    tenantId: number | string,
    articleId: number | string,
    body: { mimeType: string }
) {
    return apiRequest({
        method: 'POST',
        resource: `${tenantArticlesResource(tenantId)}/${articleId}/cover/presign`,
        body
    }) as Promise<{ presignedUrl: string; fileUrl: string }>;
}

export function confirmArticleCoverUpload(
    tenantId: number | string,
    articleId: number | string,
    body: { mimeType: string }
) {
    return apiRequest({
        method: 'POST',
        resource: `${tenantArticlesResource(tenantId)}/${articleId}/cover/confirm`,
        body
    }) as Promise<TenantArticle>;
}
